import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { Grid } from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import { useResetGeysersMutation } from "./operatorDashboardAPISlice";
import { showSuccess, showError } from "../../Global/notifications/notificationsSlice";
import { useDispatch } from "react-redux";

export default function ResetGeysers({ label = "Reset Geysers", buttonIcon }) {
  const dispatch = useDispatch();

  const [resetGeysers, { data: resetData, isSuccess: isResetSuccess, isError: isResetError }] =
    useResetGeysersMutation();

  const [open, setOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  React.useEffect(() => {
    if (isResetSuccess) {
      setConfirmOpen(false);
      dispatch(showSuccess(resetData.success));
    }
  }, [isResetSuccess]);

  React.useEffect(() => {
    if (isResetError) {
      setConfirmOpen(false);
      dispatch(showError(resetData?.error));
    }
  }, [isResetError]);

  const handleReset = () => {
    resetGeysers({ body: {} });
  };

  return (
    <>
      {/* Trigger button — styled to match the screenshot */}
      <Button
        variant="outlined"
        startIcon={buttonIcon ?? <CachedIcon sx={{ fontSize: "1rem" }} />}
        onClick={() => setOpen(true)}
        sx={{
          color: "#f87171",
          borderColor: "rgba(248,113,113,0.4)",
          fontSize: "0.82rem",
          fontWeight: 500,
          textTransform: "none",
          borderRadius: "8px",
          "&:hover": {
            borderColor: "#f87171",
            bgcolor: "rgba(248,113,113,0.08)",
          },
        }}
      >
        {label}
      </Button>

      {/* Step 1 — confirm dialog */}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#161B22",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "12px",
            color: "#e0e6f0",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", fontWeight: 600 }}>Reset Geysers</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 3, color: "rgba(255,255,255,0.65)" }}>
            This will send Relay Connect messages to all geysers that are scheduled to be on.
            Do you want to proceed?
          </Typography>
          <Grid container justifyContent="flex-end" spacing={1}>
            <Grid item>
              <Button
                variant="outlined"
                onClick={() => setOpen(false)}
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  borderColor: "rgba(255,255,255,0.2)",
                  "&:hover": { borderColor: "rgba(255,255,255,0.4)", bgcolor: "rgba(255,255,255,0.04)" },
                }}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={() => { setOpen(false); setConfirmOpen(true); }}
                sx={{
                  bgcolor: "rgba(248,113,113,0.15)",
                  color: "#f87171",
                  border: "1px solid rgba(248,113,113,0.4)",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "rgba(248,113,113,0.25)", boxShadow: "none" },
                }}
              >
                Continue
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Step 2 — final confirm */}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#161B22",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "12px",
            color: "#e0e6f0",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", fontWeight: 600 }}>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 3, color: "rgba(255,255,255,0.65)" }}>
            This action will immediately reset all geysers. This cannot be undone.
          </Typography>
          <Grid container justifyContent="flex-end" spacing={1}>
            <Grid item>
              <Button
                variant="text"
                onClick={() => setConfirmOpen(false)}
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
                }}
              >
                Back
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={handleReset}
                sx={{
                  bgcolor: "rgba(248,113,113,0.15)",
                  color: "#f87171",
                  border: "1px solid rgba(248,113,113,0.4)",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "rgba(248,113,113,0.25)", boxShadow: "none" },
                }}
              >
                Confirm Reset
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
}
